import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { useId } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Search } from 'lucide-react'
import { cn } from '@/lib/cn'

/* ------------------------------------------------------------------ *
 * Knappar
 * ------------------------------------------------------------------ */

type ButtonVariant = 'primary' | 'accent' | 'outline' | 'quiet' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-strong shadow-card',
  accent: 'bg-accent text-on-accent hover:bg-accent-strong font-bold shadow-card',
  outline: 'border-2 border-control-border text-ink hover:bg-surface-2',
  quiet: 'bg-surface-2 text-ink hover:bg-surface-3',
  ghost: 'text-ink hover:bg-surface-2',
  danger: 'bg-danger text-on-danger hover:opacity-90',
}

// Minsta höjd 44 px – Apples och Googles rekommenderade träffyta. Appen
// används med en hand medan man står i en kassakö.
const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm gap-1.5 tap-safe',
  md: 'h-11 px-4 text-base gap-2',
  lg: 'h-13 px-6 text-lg gap-2.5',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  loading?: boolean
  icon?: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth,
  loading,
  icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center rounded-pill font-semibold',
        'transition-[background-color,transform,opacity] duration-150',
        'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {loading ? <Loader2 size={18} className="animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  )
}

/** Samma utseende som Button, men navigerar i stället för att göra något. */
export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  fullWidth,
  icon,
  className,
  children,
}: {
  to: string
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  icon?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center justify-center rounded-pill font-semibold',
        'transition-[background-color,transform] duration-150 active:scale-[0.98]',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {icon}
      {children}
    </Link>
  )
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  variant?: ButtonVariant
}

export function IconButton({
  label,
  variant = 'ghost',
  className,
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-pill',
        'transition-colors duration-150 disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {children}
    </button>
  )
}

/* ------------------------------------------------------------------ *
 * Ytor
 * ------------------------------------------------------------------ */

export function Card({
  className,
  children,
  ...rest
}: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      className={cn(
        'rounded-card border border-border bg-surface shadow-card',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function SectionTitle({
  children,
  action,
}: {
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <h2 className="font-display text-lg font-extrabold">{children}</h2>
      {action}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Brickor
 * ------------------------------------------------------------------ */

type BadgeTone = 'neutral' | 'brand' | 'accent' | 'success' | 'warning' | 'danger' | 'gold'

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-muted',
  brand: 'bg-brand-soft text-brand-ink',
  accent: 'bg-accent text-on-accent',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  gold: 'bg-surface-2 text-gold',
}

export function Badge({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: BadgeTone
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2.5 py-1',
        'text-xs font-semibold whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/* ------------------------------------------------------------------ *
 * Formulär
 * ------------------------------------------------------------------ */

interface FieldProps {
  label: string
  hint?: string
  error?: string | null
  required?: boolean
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode
}

/**
 * Etikett, hjälptext och felmeddelande kopplas ihop med rätt aria-attribut,
 * så att en skärmläsare läser upp felet tillsammans med fältet.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </label>
      {hint && (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      )}
      {children({ id, describedBy })}
      {error && (
        <p id={errorId} role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

const CONTROL_CLASS = cn(
  // border-control-border, inte border-border: ett fält måste synas som ett
  // fält. Fokus använder brand-ink, annars blir ramen svagare i mörkt läge
  // när man klickar i fältet – tvärtemot vad som är meningen.
  'w-full rounded-xl border-2 border-control-border bg-surface px-3.5 py-2.5',
  'text-base text-ink placeholder:text-faint',
  'transition-colors focus:border-brand-ink focus:outline-none',
  'disabled:cursor-not-allowed disabled:opacity-60',
)

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cn(CONTROL_CLASS, 'h-12', className)} />
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cn(CONTROL_CLASS, 'min-h-28 resize-y', className)} />
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cn(CONTROL_CLASS, 'h-12 pr-8', className)}>
      {children}
    </select>
  )
}

/* ------------------------------------------------------------------ *
 * Segmentväljare – används till tema, vy-läge och statusfilter
 * ------------------------------------------------------------------ */

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T
  onChange: (value: T) => void
  options: { value: T; label: ReactNode; title?: string }[]
  label: string
  className?: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'inline-flex gap-1 rounded-pill border border-border bg-surface-2 p-1',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex min-h-9 items-center justify-center gap-1.5 rounded-pill px-3.5',
              'text-sm font-semibold transition-colors',
              selected ? 'bg-brand text-on-brand shadow-card' : 'text-muted hover:text-ink',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Tillstånd
 * ------------------------------------------------------------------ */

export function Spinner({ label = 'Laddar…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-muted" role="status">
      <Loader2 size={20} className="animate-spin" aria-hidden />
      <span className="text-sm">{label}</span>
    </div>
  )
}

/**
 * Platshållare med samma form som innehållet som är på väg.
 *
 * En spinner mitt på skärmen säger "vänta" men inte "på vad", och när
 * innehållet landar hoppar hela sidan. Ett skelett som har rätt form gör
 * väntan kortare på riktigt: ögat hinner placera sig, och landningen blir en
 * ifyllning i stället för ett hopp.
 *
 * Pulsen är undantagen från prefers-reduced-motion i index.css tillsammans
 * med spinnern – en helt stilla grå ruta läser som ett fel, inte som väntan.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-xl bg-surface-2', className)} aria-hidden />
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      {icon && <div className="text-faint">{icon}</div>}
      <h3 className="font-display text-lg font-extrabold text-ink">{title}</h3>
      {description && <p className="max-w-sm text-sm text-muted text-balance">{description}</p>}
      {action}
    </div>
  )
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-xl border border-danger/30 bg-danger-soft px-3.5 py-3 text-sm font-medium text-danger"
    >
      {children}
    </p>
  )
}

export function InfoNote({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border border-border bg-surface-2 px-3.5 py-3 text-sm text-muted">
      {children}
    </p>
  )
}

/* ------------------------------------------------------------------ *
 * Primitiver som annars skrivs om på varje sida
 * ------------------------------------------------------------------ */

/**
 * Överrubrik i versaler ovanför ett värde.
 *
 * Samma spärrade versalrad fanns kopierad på sju ställen med tre olika
 * teckenstorlekar och två olika spärrar. Skillnaderna var inte medvetna – de
 * uppstod för att någon skrev av en rad från fel granne.
 */
export function Eyebrow({
  children,
  icon,
  tone = 'muted',
  className,
}: {
  children: ReactNode
  icon?: ReactNode
  tone?: 'muted' | 'accent' | 'brand'
  className?: string
}) {
  const tones = { muted: 'text-muted', accent: 'text-accent', brand: 'text-brand-ink' }
  return (
    <p
      className={cn(
        'flex items-center gap-1.5 font-display text-[11px] font-bold tracking-[0.16em] uppercase',
        tones[tone],
        className,
      )}
    >
      {icon}
      {children}
    </p>
  )
}

/**
 * Sökfält med förstoringsglas.
 *
 * Fanns i sju kopior, alla med samma absolut positionerade ikon och samma
 * pl-11. Fyra av dem saknade aria-label.
 */
export function SearchField({
  value,
  onChange,
  placeholder,
  label,
  className,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  /** Läses upp av skärmläsare. Krävs – ett sökfält utan namn är en gåta. */
  label: string
  className?: string
}) {
  return (
    <div className={cn('relative', className)}>
      <Search
        size={18}
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-faint"
      />
      <Input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className="pl-11"
      />
    </div>
  )
}

/**
 * Nyckeltal i en ruta.
 *
 * Fanns i tre versioner – MetricCard i översikten, StatTile i lagvyn och en
 * tredje i statistiken – med olika rubrikstorlek, olika sifferstorlek och
 * olika uppsättning toner. Den här är hopslagningen av alla tre.
 */
export function StatTile({
  label,
  value,
  hint,
  icon,
  tone = 'neutral',
  size = 'md',
  className,
}: {
  label: string
  value: string
  hint?: string
  icon?: ReactNode
  tone?: 'neutral' | 'brand' | 'success' | 'warning'
  size?: 'md' | 'lg'
  className?: string
}) {
  const tones = {
    neutral: 'text-ink',
    brand: 'text-brand-ink',
    success: 'text-success',
    warning: 'text-warning',
  }
  return (
    <div
      className={cn(
        'rounded-card border border-border bg-surface shadow-card',
        size === 'lg' ? 'p-4' : 'p-3.5',
        className,
      )}
    >
      <p className="flex items-center gap-1.5 text-xs font-bold tracking-wide text-muted uppercase">
        {icon}
        {label}
      </p>
      <p
        className={cn(
          'mt-1 font-display font-extrabold tabular-nums',
          size === 'lg' ? 'text-2xl' : 'text-xl',
          tones[tone],
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-muted">{hint}</p>}
    </div>
  )
}

/**
 * Vågrät mätare.
 *
 * Fanns i fem varianter med tre olika höjder. Alla utom en saknade
 * role="progressbar", så en skärmläsare läste dem som tomma rutor.
 */
export function Meter({
  percent,
  tone = 'brand',
  size = 'md',
  onBrand,
  label,
  className,
}: {
  percent: number
  tone?: 'brand' | 'success' | 'warning' | 'accent'
  size?: 'sm' | 'md' | 'lg'
  /** Mätare som ligger på en blå yta behöver ljus botten i stället för grå. */
  onBrand?: boolean
  label: string
  className?: string
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)))
  const heights = { sm: 'h-2', md: 'h-2.5', lg: 'h-3' }
  const tones = {
    brand: 'bg-brand-surface',
    success: 'bg-success',
    warning: 'bg-warning',
    accent: 'bg-accent',
  }
  return (
    <div
      className={cn(
        'overflow-hidden rounded-pill',
        heights[size],
        onBrand ? 'bg-white/20' : 'bg-surface-2',
        className,
      )}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div
        className={cn('h-full rounded-pill transition-[width] duration-(--duration-count) ease-(--ease-out)', tones[tone])}
        style={{ width: `${clamped}%` }}
      />
    </div>
  )
}

/** Skelett i formen av en erbjudanderad, som listan i häftet. */
export function OfferSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2.5" role="status" aria-label="Hämtar erbjudanden">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 rounded-card border border-border bg-surface p-3 shadow-card"
        >
          <Skeleton className="size-12 shrink-0 rounded-2xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3 rounded-pill" />
            <Skeleton className="h-3 w-2/5 rounded-pill" />
          </div>
          <Skeleton className="h-7 w-16 shrink-0 rounded-pill" />
        </div>
      ))}
    </div>
  )
}
